import React from 'react'
import useOfferListFilters from 'hooks/Offers/useOfferListFilters'
import { nonNullable } from 'lib/array/arrayUtils'
import { HolidayTypeToIconMap, encodeSearchParams } from 'lib/search/searchUtils'
import { useMemo } from 'react'
import config from 'constants/config'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FullWidthCarousel from 'components/Luxkit/Carousel/FullWidthCarousel'
import ToggleButton from 'components/Luxkit/ToggleButton'
import Heading from 'components/Luxkit/Typography/Heading'
import LineSwimmerIcon from 'components/Luxkit/Icons/line/LineSwimmerIcon'
import LineSunsetIcon from 'components/Luxkit/Icons/line/LineSunsetIcon'
import LineWaterIcon from 'components/Luxkit/Icons/line/LineWaterIcon'
import LineMoneyStackIcon from 'components/Luxkit/Icons/line/LineMoneyStackIcon'
import LineFamilyIcon from 'components/Luxkit/Icons/line/LineFamilyIcon'
import LineMountainsSunIcon from 'components/Luxkit/Icons/line/LineMountainsSunIcon'
import LineFireIcon from 'components/Luxkit/Icons/line/LineFireIcon'
import LineFlowerIcon from 'components/Luxkit/Icons/line/LineFlowerIcon'
import LineSkiLiftIcon from 'components/Luxkit/Icons/line/LineSkiLiftIcon'
import LineUsersAltIcon from 'components/Luxkit/Icons/line/LineUsersAltIcon'
import LineParkingIcon from 'components/Luxkit/Icons/line/LineParkingIcon'
import LineOvenIcon from 'components/Luxkit/Icons/line/LineOvenIcon'
import { HOMES_AND_VILLAS_FILTERS } from 'constants/offerList'

interface Collection {
  title: string;
  type: 'all' | 'amenity' | 'sort' | 'holidayType';
  value: string;
  icon: React.ReactNode;
}

const collectionDefinitions: Array<Collection> = [
  { title: 'All', type: 'all', value: 'all', icon: HolidayTypeToIconMap.All },
  { title: 'Private pool', type: 'amenity', value: 'Private pool', icon: <LineSwimmerIcon /> },
  { title: 'Beach', type: 'holidayType', value: 'Sun & Beach', icon: <LineSunsetIcon /> },
  { title: 'Ocean view', type: 'amenity', value: 'Ocean views', icon: <LineWaterIcon /> },
  { title: 'Best value', type: 'sort', value: 'value', icon: <LineMoneyStackIcon /> },
  { title: 'Family friendly', type: 'holidayType', value: 'Family Friendly', icon: <LineFamilyIcon /> },
  { title: 'Mountain views', type: 'amenity', value: 'Mountain views', icon: <LineMountainsSunIcon /> },
  { title: 'Trending', type: 'sort', value: 'sales', icon: <LineFireIcon /> },
  { title: 'Fully equipped kitchen', type: 'amenity', value: 'Kitchen', icon: <LineOvenIcon /> },
  { title: 'Outdoor space', type: 'amenity', value: 'Outdoor space', icon: <LineFlowerIcon /> },
  { title: 'Ski & snow', type: 'holidayType', value: 'Ski & Snow', icon: <LineSkiLiftIcon /> },
  { title: 'Big groups', type: 'holidayType', value: 'Group', icon: <LineUsersAltIcon /> },
  { title: 'Private parking', type: 'amenity', value: 'Private parking', icon: <LineParkingIcon /> },
]

const minimumRequirement = 15

interface Props {
  filters: App.OfferListFilters;
}

function HomesAndVillasVerticalPageCollections(props: Props) {
  const { filters } = props
  const filterOptions = useOfferListFilters(filters)

  const collections = useMemo(() => {
    const baseQuery = encodeSearchParams({
      urlSearch: '',
      searchItem: ANYWHERE_SEARCH_ITEM,
      rooms: [config.search.defaultOccupants],
      isFlexibleWithDate: true,
      filters: HOMES_AND_VILLAS_FILTERS,
    })

    return nonNullable(collectionDefinitions.map(def => {
      let query: URLSearchParams
      let count: number
      switch (def.type) {
        case 'all' :
          return {
            title: 'All',
            icon: def.icon,
            to: `/search/homes-and-villas?${baseQuery}`,
          }
        case 'holidayType':
          count = filterOptions.filters?.holidayTypes[def.value] ?? 0
          if (count < minimumRequirement) {
            return undefined
          }

          query = new URLSearchParams(baseQuery)
          query.set('holidayTypes', def.value)

          return {
            title: def.title,
            icon: def.icon,
            to: `/search/homes-and-villas?${query}`,
          }
        case 'amenity':
          count = filterOptions.filters?.amenities[def.value] ?? 0
          if (count < minimumRequirement) {
            return undefined
          }

          query = new URLSearchParams(baseQuery)
          query.set('amenities', def.value)

          return {
            title: def.title,
            icon: def.icon,
            to: `/search/homes-and-villas?${query}`,
          }
        case 'sort':
          query = new URLSearchParams(baseQuery)
          query.set('sortBy', def.value)

          return {
            title: def.title,
            icon: def.icon,
            to: `/search/homes-and-villas?${query}`,
          }
      }
    }))
  }, [filterOptions])

  return <VerticalSpacer gap={32}>
    <LayoutContainer>
      <Heading variant="heading2">
        Browse by <i>collection</i>
      </Heading>
    </LayoutContainer>
    <FullWidthCarousel itemsPerArrow={6}>
      {collections.map((collection =>
        <ToggleButton
          key={collection.title}
          kind="primary"
          orientation="vertical"
          startIcon={collection.icon}
          title={collection.title}
          to={collection.to}
          target="_blank"
        />
      ))}
    </FullWidthCarousel>
  </VerticalSpacer>
}

export default HomesAndVillasVerticalPageCollections
